import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Paper,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);

const RangeDay = styled(PickersDay, {
  shouldForwardProp: (prop) =>
    !["isStart", "isEnd", "isInRange", "accentColor", "rangeColor"].includes(
      prop,
    ),
})(({ theme, isStart, isEnd, isInRange, accentColor, rangeColor }) => {
  const accent = accentColor || theme.palette.primary.main;
  const strip = rangeColor || theme.palette.primary.light;
  const isEdge = isStart || isEnd;
  const hasStrip = isStart || isEnd || isInRange;
  const isSingleDay = isStart && isEnd;

  return {
    borderRadius: "50%",
    position: "relative",
    zIndex: 1,
    "&::before": {
      content: hasStrip && !isSingleDay ? '""' : "none",
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      height: 36,
      zIndex: -1,
      backgroundColor: strip,
      left: isStart && !isEnd ? "50%" : 0,
      right: isEnd && !isStart ? "50%" : 0,
    },
    ...(isEdge && {
      backgroundColor: `${accent} !important`,
      color: `${theme.palette.getContrastText(accent)} !important`,
      fontWeight: 600,
      "&:hover, &:focus": {
        backgroundColor: `${accent} !important`,
        filter: "brightness(0.88)",
      },
    }),
    ...(isInRange &&
      !isEdge && {
        backgroundColor: "transparent !important",
        color: theme.palette.text.primary,
        borderRadius: 0,
        "&:hover": {
          backgroundColor: `${strip} !important`,
          borderRadius: 0,
        },
      }),
  };
});

const buildDaySlot = (startDate, endDate, accentColor, rangeColor) => {
  function CustomDay({ day, outsideCurrentMonth, ...rest }) {
    const inCurrentMonth = !outsideCurrentMonth;
    const isStart =
      inCurrentMonth && Boolean(startDate) && day.isSame(startDate, "day");
    const isEnd =
      inCurrentMonth && Boolean(endDate) && day.isSame(endDate, "day");
    const isInRange =
      inCurrentMonth &&
      Boolean(startDate) &&
      Boolean(endDate) &&
      day.isBetween(startDate, endDate, "day", "()");

    return (
      <RangeDay
        {...rest}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        isStart={isStart}
        isEnd={isEnd}
        isInRange={isInRange}
        accentColor={accentColor}
        rangeColor={rangeColor}
        selected={false}
      />
    );
  }

  CustomDay.displayName = "DateRangeDay";
  return CustomDay;
};

const DateRangeFilter = ({
  label = "Select Date Range",
  onDateRangeChange,
  dateType = "created_date",
  displayFormat = "MM/DD/YYYY",
  outputFormat = "YYYY-MM-DD",
  accentColor,
  rangeColor,
  sx = {},
  value = null,
}) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectingStart, setSelectingStart] = useState(true);
  const [cal1Month, setCal1Month] = useState(dayjs());
  const [cal2Month, setCal2Month] = useState(dayjs().add(1, "month"));

  const buildPayload = useCallback(
    (start, end) => ({
      startDate: start ? start.toISOString() : null,
      endDate: end ? end.endOf("day").toISOString() : null,
      [`${dateType}__gte`]: start ? start.format(outputFormat) : null,
      [`${dateType}__lte`]: end ? end.format(outputFormat) : null,
    }),
    [dateType, outputFormat],
  );

  useEffect(() => {
    const rawStart = value?.[`${dateType}__gte`] ?? null;
    const rawEnd = value?.[`${dateType}__lte`] ?? null;

    if (!rawStart && !rawEnd) {
      setStartDate(null);
      setEndDate(null);
      setSelectingStart(true);
      setCal1Month(dayjs());
      setCal2Month(dayjs().add(1, "month"));
      return;
    }

    const nextStart = rawStart ? dayjs(rawStart) : null;
    const nextEnd = rawEnd ? dayjs(rawEnd) : null;
    const validStart = nextStart?.isValid() ? nextStart : null;
    const validEnd = nextEnd?.isValid() ? nextEnd : null;

    setStartDate(validStart);
    setEndDate(validEnd);
    setSelectingStart(!(validStart && validEnd));

    if (validStart) {
      setCal1Month(validStart);
      setCal2Month(validStart.add(1, "month"));
    }
  }, [dateType, value]);

  const onCal1MonthChange = useCallback((nextMonth) => {
    setCal1Month(nextMonth);
    setCal2Month(nextMonth.add(1, "month"));
  }, []);

  const onCal2MonthChange = useCallback((nextMonth) => {
    setCal2Month(nextMonth);
    setCal1Month(nextMonth.subtract(1, "month"));
  }, []);

  const handleDateSelect = useCallback(
    (nextDate) => {
      if (!nextDate) return;

      if (selectingStart) {
        setStartDate(nextDate);
        setEndDate(null);
        setSelectingStart(false);
        setCal1Month(nextDate);
        setCal2Month(nextDate.add(1, "month"));
        return;
      }

      const [finalStart, finalEnd] = nextDate.isBefore(startDate, "day")
        ? [nextDate, startDate]
        : [startDate, nextDate];

      setStartDate(finalStart);
      setEndDate(finalEnd);
      setSelectingStart(true);
      onDateRangeChange?.(buildPayload(finalStart, finalEnd));
    },
    [buildPayload, onDateRangeChange, selectingStart, startDate],
  );

  const clearSelection = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    setSelectingStart(true);
    setCal1Month(dayjs());
    setCal2Month(dayjs().add(1, "month"));
    onDateRangeChange?.(buildPayload(null, null));
  }, [buildPayload, onDateRangeChange]);

  const handleOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    if (startDate && !endDate) {
      setStartDate(null);
      setSelectingStart(true);
    }
    setAnchorEl(null);
  }, [endDate, startDate]);

  const handleApply = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const displayText = useMemo(() => {
    if (!startDate) return "";
    if (!endDate) return `${startDate.format(displayFormat)} -> pick end`;
    return `${startDate.format(displayFormat)} - ${endDate.format(
      displayFormat,
    )}`;
  }, [displayFormat, endDate, startDate]);

  const selectedDayCount = useMemo(() => {
    if (!startDate || !endDate) return 0;
    return endDate.diff(startDate, "day");
  }, [endDate, startDate]);

  const DaySlot = useMemo(
    () => buildDaySlot(startDate, endDate, accentColor, rangeColor),
    [accentColor, endDate, rangeColor, startDate],
  );

  const open = Boolean(anchorEl);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: "100%", ...sx }}>
        <TextField
          label={label}
          value={displayText}
          onClick={handleOpen}
          size="small"
          fullWidth
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <CalendarTodayIcon
                  sx={{
                    fontSize: 18,
                    color: "action.active",
                    cursor: "pointer",
                  }}
                />
              </InputAdornment>
            ),
            sx: { cursor: "pointer" },
          }}
          InputLabelProps={{ shrink: Boolean(displayText) || open }}
          sx={{
            maxWidth: 400,
            "& .MuiInputBase-input": {
              cursor: "pointer",
              fontSize: "0.875rem",
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.875rem",
            },
          }}
        />

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{ elevation: 4, sx: { borderRadius: 2, mt: 0.5 } }}
        >
          <Paper elevation={0} sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color={
                    selectingStart && !startDate
                      ? "primary.main"
                      : "text.secondary"
                  }
                >
                  {selectingStart ? "Select start date" : "Select end date"}
                </Typography>
                {(startDate || endDate) && (
                  <Button size="small" color="error" onClick={clearSelection}>
                    Clear
                  </Button>
                )}
              </Box>

              {(startDate || endDate) && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {startDate && (
                    <Chip
                      label={`Start: ${startDate.format(displayFormat)}`}
                      size="small"
                      color="primary"
                      variant={endDate ? "filled" : "outlined"}
                    />
                  )}
                  {endDate && (
                    <Chip
                      label={`End: ${endDate.format(displayFormat)}`}
                      size="small"
                      color="primary"
                      variant="filled"
                    />
                  )}
                  {startDate && endDate && (
                    <Chip
                      label={`${selectedDayCount} day${
                        selectedDayCount !== 1 ? "s" : ""
                      }`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Stack>
              )}

              <Stack
                direction={{ xs: "column", sm: "row" }}
                divider={
                  <Box
                    sx={{
                      width: { sm: "1px" },
                      bgcolor: "divider",
                      alignSelf: "stretch",
                    }}
                  />
                }
                sx={{
                  "& .MuiPickersDay-root.Mui-selected": {
                    backgroundColor: "transparent !important",
                    color: "inherit",
                  },
                  "& .MuiPickersLayout-root": {
                    minWidth: 290,
                  },
                }}
              >
                <StaticDatePicker
                  displayStaticWrapperAs="desktop"
                  value={null}
                  onChange={handleDateSelect}
                  referenceDate={cal1Month}
                  onMonthChange={onCal1MonthChange}
                  slots={{ day: DaySlot }}
                  slotProps={{
                    actionBar: { actions: [] },
                    nextIconButton: { disabled: true },
                  }}
                />

                <StaticDatePicker
                  displayStaticWrapperAs="desktop"
                  value={null}
                  onChange={handleDateSelect}
                  referenceDate={cal2Month}
                  onMonthChange={onCal2MonthChange}
                  slots={{ day: DaySlot }}
                  slotProps={{
                    actionBar: { actions: [] },
                    previousIconButton: { disabled: true },
                  }}
                />
              </Stack>

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button size="small" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleApply}
                  disabled={!startDate || !endDate}
                >
                  Apply
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Popover>
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangeFilter;
